// "Found the right part? Paste the part number or link" (2026-10-30,
// correctPart.js). The CSR pastes a SKU, a brand / OE / vendor part number, a
// 1A or AM product link, or an eBay item link or number; the server resolves
// it and checks its fitment (POST /api/ai/suggestions/:id/correct-part,
// reads only). Fits: onUse(result) switches the reply. Doesn't fit, not
// found, fitment unavailable: the reason shows here and nothing changes;
// unavailable offers "Use it anyway". The same box on the main panel and the
// admin shadow column.
import { useState } from 'react'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { findCorrectPart } from './api.js'
import { BOX_LABEL, resultLine } from './correctPart.js'

export default function CorrectPartBox({ suggestionId, onUse, testId = 'correct-part-box', find = findCorrectPart }) {
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const run = async (useAnyway = false) => {
    if (!input.trim() || !suggestionId) return
    setBusy(true)
    setError(null)
    try {
      const r = await find(suggestionId, { input: input.trim(), useAnyway })
      setResult(r)
      if (r?.reply && (r.status === 'fits' || r.status === 'fits_unconfirmed')) await onUse?.(r)
    } catch (e) {
      setError(e?.message ?? 'The lookup failed.')
    } finally {
      setBusy(false)
    }
  }
  const line = resultLine(result)
  return (
    <Box data-testid={testId} sx={{ mt: 0.5, mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>{BOX_LABEL}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField size="small" fullWidth value={input} onChange={(e) => { setInput(e.target.value); setResult(null) }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); run(false) } }}
          inputProps={{ 'data-testid': `${testId}-input`, 'aria-label': BOX_LABEL }} disabled={busy} />
        <Button size="small" variant="outlined" onClick={() => run(false)} disabled={busy || !input.trim()} data-testid={`${testId}-check`}>Check</Button>
      </Stack>
      {line && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Typography data-testid={`${testId}-message`} sx={{ fontSize: 12, color: 'warning.main' }}>{line}</Typography>
          {result?.canUseAnyway && (
            <Button size="small" onClick={() => run(true)} disabled={busy} data-testid={`${testId}-use-anyway`}>Use it anyway</Button>
          )}
        </Stack>
      )}
      {result?.reply && <Typography data-testid={`${testId}-used`} sx={{ fontSize: 12, color: 'success.main', mt: 0.5 }}>{result.message}</Typography>}
      {error && <Typography data-testid={`${testId}-error`} sx={{ fontSize: 12, color: 'error.main', mt: 0.5 }}>{error}</Typography>}
    </Box>
  )
}
